import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import PageNavbar from './PageNavbar';
import PageCloudHero from './PageCloudHero';
import Footer from '../../pages/home/components/Footer';
import { PATHS } from '../../router/paths';

type LegalPageLayoutProps = {
  badge: string;
  badgeIcon: string;
  title: string;
  lead: string;
  supporting: string;
  lastUpdated: string;
  children: ReactNode;
};

const legalLinks = [
  { to: PATHS.ochranaOsobnychUdajov, label: 'Ochrana osobných údajov' },
  { to: PATHS.cookies, label: 'Cookies' },
  { to: PATHS.pravneInformacie, label: 'Právne informácie' },
  { to: PATHS.podmienkyPouzivania, label: 'Podmienky používania' },
];

export function LegalSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-[30px] border border-white/10 bg-[linear-gradient(180deg,rgba(26,26,26,0.92)_0%,rgba(18,18,18,0.86)_100%)] p-7 shadow-[0_20px_56px_rgba(0,0,0,0.28)] backdrop-blur-xl md:p-9">
      <h2 className="text-2xl font-caprasimo text-white md:text-[2rem]">{title}</h2>
      <div className="mt-5 space-y-4 text-[15px] leading-8 text-white/72 md:text-base">
        {children}
      </div>
    </section>
  );
}

export function LegalList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex gap-3">
          <span className="mt-2 h-2 w-2 flex-none rounded-full bg-brand-green/80" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function LegalPageLayout({
  badge,
  badgeIcon,
  title,
  lead,
  supporting,
  lastUpdated,
  children,
}: LegalPageLayoutProps) {
  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <PageNavbar />

      <PageCloudHero
        badge={badge}
        badgeIcon={badgeIcon}
        title={title}
        lead={lead}
        supporting={supporting}
      />

      <main className="relative z-20 -mt-12 pb-24 md:-mt-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-8 rounded-[30px] border border-white/10 bg-[linear-gradient(180deg,rgba(26,26,26,0.92)_0%,rgba(18,18,18,0.86)_100%)] p-6 shadow-[0_20px_56px_rgba(0,0,0,0.28)] backdrop-blur-xl md:p-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/42">
                  Posledná aktualizácia
                </p>
                <p className="mt-2 text-sm text-white/72 md:text-base">{lastUpdated}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                {legalLinks.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-sm font-medium text-white/76 transition-all hover:bg-white/[0.08] hover:text-white"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-8">{children}</div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
